import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { Loader2 } from "lucide-react";
import { useLocation } from "wouter";

interface ContactFormProps {
  onSuccess?: () => void;
}

export default function ContactForm({ onSuccess }: ContactFormProps) {
  const [, setLocation] = useLocation();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState(""); // Hidden spam trap
  const [notRobot, setNotRobot] = useState(false);

  const submitContact = trpc.contact.submit.useMutation({
    onSuccess: () => {
      // Track Google Ads conversion
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }
      onSuccess?.();
      // Redirect to Contact Thank You page
      setLocation("/contact-thank-you");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!notRobot) {
      return; // Don't submit if checkbox not checked
    }

    submitContact.mutate({
      name,
      phone,
      email,
      message,
      honeypot, // This should be empty for real users
    });
  };

  return (
    <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-sm relative">
      <CardContent className="p-6 md:p-8 space-y-6">
        <div className="space-y-2 text-center">
          <h3 className="text-2xl font-serif font-bold text-[oklch(0.2_0.03_145)]">Send Us a Message</h3>
          <p className="text-[oklch(0.4_0.03_145)] text-sm">Have a question? We'd love to hear from you.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Honeypot field - hidden from real users, bots will fill it */}
          <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <Input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="contactName" className="text-sm font-medium text-[oklch(0.35_0.05_145)]">
              Your Name *
            </label>
            <Input
              id="contactName"
              autoComplete="name"
              placeholder="John Smith"
              className="h-12 bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="contactPhone" className="text-sm font-medium text-[oklch(0.35_0.05_145)]">
              Phone Number *
            </label>
            <Input
              id="contactPhone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(306) 555-1234"
              className="h-12 bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="contactEmail" className="text-sm font-medium text-[oklch(0.35_0.05_145)]">
              Email Address *
            </label>
            <Input
              id="contactEmail"
              type="email"
              autoComplete="email"
              placeholder="john@example.com"
              className="h-12 bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <label htmlFor="contactMessage" className="text-sm font-medium text-[oklch(0.35_0.05_145)]">
              Your Message *
            </label>
            <Textarea
              id="contactMessage"
              placeholder="How can we help you?"
              className="min-h-[120px] bg-stone-50 border-stone-200 focus:border-[oklch(0.35_0.12_145)] focus:ring-[oklch(0.35_0.12_145)]"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>

          {/* Human verification checkbox */}
          <div className="flex items-start space-x-3 py-2">
            <Checkbox
              id="contactNotRobot"
              checked={notRobot}
              onCheckedChange={(checked) => setNotRobot(checked === true)}
              className="mt-1"
            />
            <label
              htmlFor="contactNotRobot"
              className="text-sm text-[oklch(0.4_0.03_145)] cursor-pointer leading-relaxed"
            >
              I confirm I am not a robot.
            </label>
          </div>

          {submitContact.error && (
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded-md">
              {submitContact.error.message?.includes("phone") || submitContact.error.message?.includes("Phone")
                ? "Please enter a valid phone number (e.g., 306-555-1234)"
                : submitContact.error.message?.includes("email") || submitContact.error.message?.includes("Email")
                ? "Please enter a valid email address"
                : "Something went wrong. Please try again."}
            </p>
          )}

          <Button
            type="submit"
            disabled={!notRobot || submitContact.isPending}
            className="w-full h-12 text-lg font-bold bg-[oklch(0.75_0.15_85)] hover:bg-[oklch(0.65_0.15_85)] text-[oklch(0.2_0.03_145)] shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitContact.isPending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Send Message"
            )}
          </Button>

          <p className="text-xs text-center text-[oklch(0.5_0.02_145)] pt-2">
            We respect your privacy. Your info is never sold.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
